// Spec 28 PR 1 — MultiInputCard (team invites).
//
// Signature element: paste-driven row materialization. The user
// can type one email per line, but the optimized path is "paste a
// list, watch rows appear". On paste, the input parses by [\n,\s],
// drops invalid rows into a quiet "fix these" group, and each
// valid row stagger-fades in.
//
// State-machine visuals:
//   idle: empty list with a placeholder textarea.
//   dirty: rows present; pill shows the count.
//   submitting: pill spinner, rows dim.
//   succeeded: trace line ("✓ Invited 3 teammates").
//   failed: rows stay; row-level errors render under each.

import { ArrowRight, Loader2, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Pill } from "@/components/glass";

import type { WidgetRenderProps } from "../registry";

interface MultiInputCardProps {
	title: string;
	body?: string | null;
	fieldKind: "email";
	placeholder: string;
	defaultRole: string;
	roleOptions: { id: string; label: string }[];
	confirmLabel: string;
	cancelLabel: string | null;
}

interface Row {
	id: string;
	value: string;
	role: string;
	valid: boolean;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SPLIT_RE = /[\s,;\n]+/;

export function MultiInputCard({
	props,
	state,
	onAction,
}: WidgetRenderProps<MultiInputCardProps>) {
	const [rows, setRows] = useState<Row[]>([]);
	const [draft, setDraft] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);
	const submitting = state.kind === "submitting";
	const succeeded = state.kind === "succeeded";
	const failed = state.kind === "failed";

	const validRows = useMemo(() => rows.filter((r) => r.valid), [rows]);
	const invalidRows = useMemo(() => rows.filter((r) => !r.valid), [rows]);

	useEffect(() => {
		if (rows.length === 0) return;
		// Flip to dirty once there's at least one row.
		onAction("dirty", { count: validRows.length });
	}, [rows.length, validRows.length, onAction]);

	if (succeeded) {
		const r = state.result as { invited?: number } | undefined;
		const count = r?.invited ?? validRows.length;
		// Server emits succeeded for both confirm and cancel paths
		// (`result.invited === 0` on cancel). Show a neutral "skipped"
		// trace on cancel rather than a misleading green-check
		// "Invited 0 teammates."
		if (count === 0) {
			return (
				<div className="text-center py-2">
					<span className="text-[12px] text-[var(--color-text-dim)]">
						Skipped team invites
					</span>
				</div>
			);
		}
		return (
			<div className="text-center py-2">
				<span className="text-[12px] text-[var(--color-text-muted)]">
					<span className="text-[var(--color-success)] mr-1.5">✓</span>
					Invited {count} {count === 1 ? "teammate" : "teammates"}
				</span>
			</div>
		);
	}

	const commitDraft = (raw: string) => {
		const tokens = raw.split(SPLIT_RE).filter(Boolean);
		if (tokens.length === 0) return;
		const existing = new Set(rows.map((r) => r.value.toLowerCase()));
		const newRows: Row[] = [];
		for (const tok of tokens) {
			const v = tok.toLowerCase();
			if (existing.has(v)) continue;
			existing.add(v);
			newRows.push({
				id: `r_${rows.length + newRows.length}_${Date.now()}`,
				value: tok,
				role: props.defaultRole,
				valid: EMAIL_RE.test(tok),
			});
		}
		if (newRows.length > 0) {
			setRows((prev) => [...prev, ...newRows]);
		}
		setDraft("");
	};

	const removeRow = (id: string) => {
		setRows((prev) => prev.filter((r) => r.id !== id));
	};

	const submit = () => {
		if (submitting || validRows.length === 0) return;
		onAction("confirm", {
			emails: validRows.map((r) => r.value),
			role: validRows[0]!.role,
		});
	};

	return (
		<div
			className="py-8 space-y-6"
			style={{ opacity: submitting ? 0.8 : 1, transition: "opacity 200ms" }}
		>
			<div className="text-center space-y-2">
				<div className="text-[24px] font-medium tracking-tight text-[var(--color-text)] anim-fade-rise">
					{props.title}
				</div>
				{props.body && (
					<div
						className="text-[13px] text-[var(--color-text-muted)] max-w-md mx-auto leading-relaxed anim-fade-in"
						style={{ animationDelay: "120ms" }}
					>
						{props.body}
					</div>
				)}
			</div>

			<div
				className="max-w-[480px] mx-auto space-y-3 anim-fade-rise"
				style={{ animationDelay: "200ms" }}
			>
				<input
					ref={inputRef}
					type="text"
					value={draft}
					onChange={(e) => setDraft(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === ",") {
							e.preventDefault();
							commitDraft(draft);
						} else if (e.key === "Backspace" && draft === "" && rows.length > 0) {
							removeRow(rows[rows.length - 1]!.id);
						}
					}}
					onPaste={(e) => {
						const text = e.clipboardData.getData("text");
						if (text && SPLIT_RE.test(text)) {
							e.preventDefault();
							commitDraft(text);
						}
					}}
					onBlur={() => {
						if (draft.trim()) commitDraft(draft);
					}}
					placeholder={rows.length === 0 ? props.placeholder : "add another…"}
					disabled={submitting}
					className="w-full h-10 px-3 rounded-md bg-[var(--color-surface-solid)] border border-[var(--color-border)] text-[13px] outline-none hover:border-[var(--color-border-hi)] focus:border-[var(--color-border-hi)] disabled:opacity-40"
				/>

				{rows.length > 0 && (
					<div className="space-y-1">
						{rows.map((row, i) => (
							<RowItem
								key={row.id}
								row={row}
								index={i}
								onRemove={() => removeRow(row.id)}
								disabled={submitting}
							/>
						))}
					</div>
				)}

				{invalidRows.length > 0 && (
					<div className="text-[11px] text-[var(--color-warning,#f59e0b)]">
						{invalidRows.length}{" "}
						{invalidRows.length === 1 ? "entry" : "entries"} can't be sent
						— remove or fix.
					</div>
				)}
			</div>

			<div className="flex flex-col items-center gap-3">
				<Pill
					onClick={submit}
					disabled={
						submitting || validRows.length === 0 || invalidRows.length > 0
					}
					className="!h-11 !px-5 !text-[14px]"
				>
					{submitting ? (
						<Loader2 className="w-4 h-4 animate-spin" />
					) : (
						<>
							{failed
								? "Try again"
								: validRows.length === 0
									? props.confirmLabel
									: `${props.confirmLabel} (${validRows.length})`}
							<ArrowRight className="w-4 h-4" />
						</>
					)}
				</Pill>
				{props.cancelLabel && !submitting && (
					<button
						type="button"
						onClick={() => onAction("cancel")}
						className="text-[12px] text-[var(--color-text-dim)] hover:text-[var(--color-text-muted)] transition-colors"
					>
						{props.cancelLabel}
					</button>
				)}
			</div>
		</div>
	);
}

function RowItem({
	row,
	index,
	onRemove,
	disabled,
}: {
	row: Row;
	index: number;
	onRemove: () => void;
	disabled?: boolean;
}) {
	return (
		<div
			className="flex items-center gap-2 px-3 h-8 rounded-md bg-white/[0.02] border border-[var(--color-border)] anim-fade-rise"
			style={{
				animationDelay: `${Math.min(index * 40, 240)}ms`,
				borderColor: row.valid
					? "var(--color-border)"
					: "rgba(245,158,11,0.35)",
			}}
		>
			<span
				className={`text-[12px] mono flex-1 truncate ${
					row.valid
						? "text-[var(--color-text)]"
						: "text-[var(--color-warning,#f59e0b)]"
				}`}
			>
				{row.value}
			</span>
			<button
				type="button"
				onClick={onRemove}
				disabled={disabled}
				className="w-5 h-5 flex items-center justify-center text-[var(--color-text-dim)] hover:text-[var(--color-text-muted)] transition-colors disabled:opacity-40"
				aria-label="Remove"
			>
				<X className="w-3 h-3" />
			</button>
		</div>
	);
}
